import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Button, Typography } from '@material-ui/core';
import { ArrowBack as BackIcon } from '@material-ui/icons';
import PropTypes from 'prop-types';

import { TableListView } from 'components';
import ConfirmModal from 'Views/Common/ConfirmModal';
import { Context } from 'context';
import { ADD_NOTIFICATION } from 'context/actions';
import { SSO_SERVICES } from 'Services';
import Utils from 'Shared/Utils';
import { PERMISSIONS_LABEL } from 'Views/Common/enum';

import { GROUP_IDENTIFIER_LABEL, IDENTIFIER_TYPE, getGroupKeyField } from './constants';
import GroupMappingFormModal from './GroupMappingFormModal';

const formatRole = (role) => {
  if (!role) return '-';
  return role
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

const getLevelFromRole = (role) => {
  if (!role) return '-';
  if (role.startsWith('partner')) return 'Partner';
  if (role.startsWith('group')) return 'Group';
  if (role.startsWith('region')) return 'Region';
  if (role.startsWith('location')) return 'Location';
  return '-';
};

const formatLevelIds = (mapping) => {
  if (mapping.level_id_details?.length) {
    return mapping.level_id_details.map((d) => d.name).join(', ');
  }
  if (mapping.level_ids?.length) {
    return mapping.level_ids.join(', ');
  }
  return '-';
};

const formatFeatures = (features) => {
  if (!features?.length) return '-';
  return features.map((key) => PERMISSIONS_LABEL[key] || key).join(', ');
};

const SSOGroupMappingSection = ({ level, levelId, partnerId, groupId, ssoProvider, identifierType, onBack }) => {
  const { dispatch } = useContext(Context);

  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editMapping, setEditMapping] = useState(null);
  const [deleteMapping, setDeleteMapping] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [levelName, setLevelName] = useState('');

  useEffect(() => {
    fetchMappings();
  }, []);

  const setNotification = (severity, message) => {
    dispatch({
      type: ADD_NOTIFICATION,
      payload: { notification: { severity, message } },
    });
  };

  const fetchMappings = async () => {
    try {
      const response = await SSO_SERVICES.getGroupMappings(level, levelId, ssoProvider);

      if (Utils.checkIfSuccess(response)) {
        setMappings(response?.data?.mappings || []);
        setLevelName(response?.data?.level_name || '');
      }
    } catch (error) {
      console.error('Failed to load group mappings:', error);
      setNotification('error', 'Failed to load group mappings');
    }
    setLoading(false);
  };

  const handleAddClick = () => {
    setEditMapping(null);
    setIsFormOpen(true);
  };

  const handleEditClick = (mapping) => {
    setEditMapping(mapping);
    setIsFormOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditMapping(null);
  };

  const handleFormSave = () => {
    handleFormClose();
    setLoading(true);
    fetchMappings();
  };

  const handleDeleteConfirm = async () => {
    if (!deleteMapping) return;
    setDeleteLoading(true);

    try {
      const response = await SSO_SERVICES.deleteGroupMapping(deleteMapping.id);

      if (Utils.checkIfSuccess(response)) {
        setNotification('success', 'Group mapping deleted successfully');
        setDeleteMapping(null);
        setLoading(true);
        fetchMappings();
      } else {
        setNotification('error', response?.message || 'Failed to delete group mapping');
      }
    } catch (error) {
      console.error('Error deleting group mapping:', error);
      setNotification('error', 'An error occurred while deleting group mapping');
    }
    setDeleteLoading(false);
  };

  const groupIdentifierColumn = {
    name: 'idp_group_identifier',
    label: GROUP_IDENTIFIER_LABEL[identifierType] || GROUP_IDENTIFIER_LABEL[IDENTIFIER_TYPE.ID],
  };

  const tableColumns = [
    groupIdentifierColumn,
    { name: 'internal_role', label: 'Role' },
    { name: 'level', label: 'Level' },
    { name: 'assigned_entities', label: 'Assigned Entities' },
    { name: 'access_scope', label: 'Scope' },
    { name: 'features', label: 'Features' },
    {
      name: 'actions',
      label: 'Actions',
      options: {
        sort: false,
        customBodyRender: (mapping) => (
          <>
            <Typography
              component="span"
              className="theme_color_orange cursor_pointer"
              onClick={(e) => {
                e.stopPropagation();
                handleEditClick(mapping);
              }}
              data-testid={`edit-mapping-${mapping.id}`}
            >
              Edit
            </Typography>
            <Typography component="span" className="separator">
              |
            </Typography>
            <Typography
              component="span"
              className="theme_color_orange cursor_pointer"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteMapping(mapping);
              }}
              data-testid={`delete-mapping-${mapping.id}`}
            >
              Delete
            </Typography>
          </>
        ),
      },
    },
  ];

  const tableData = useMemo(
    () =>
      mappings.map((mapping) => ({
        idp_group_identifier: mapping[getGroupKeyField(identifierType)] || '-',
        internal_role: formatRole(mapping.internal_role),
        level: getLevelFromRole(mapping.internal_role),
        assigned_entities: formatLevelIds(mapping),
        access_scope: mapping.access_scope || 'full',
        features: formatFeatures(mapping.features),
        actions: mapping,
      })),
    [mappings, identifierType]
  );

  const headerTitle = levelName ? `${levelName} — Group Mappings` : 'Group Mappings';

  return (
    <>
      <TableListView
        tableData={tableData}
        tableColumns={tableColumns}
        tableOptions={{}}
        pagination={tableData.length > 10}
        headerSection={true}
        headerTitle={headerTitle}
        headerButton="+ Add Mapping"
        handleHeaderButtonClick={handleAddClick}
        emptyPlaceHolderContent="No group mappings configured yet"
        isLoading={loading}
        otherButtons={
          <Button
            size="large"
            color="secondary"
            onClick={onBack}
            startIcon={<BackIcon />}
            style={{ marginRight: 8 }}
            data-testid="back-to-configs"
          >
            Back
          </Button>
        }
      />

      {isFormOpen && (
        <GroupMappingFormModal
          level={level}
          levelId={levelId}
          partnerId={partnerId}
          groupId={groupId}
          ssoProvider={ssoProvider}
          identifierType={identifierType}
          mappingData={editMapping}
          onClose={handleFormClose}
          onSave={handleFormSave}
        />
      )}

      {deleteMapping && (
        <ConfirmModal
          isOpen={true}
          closeModal={() => setDeleteMapping(null)}
          emitConfirmEvent={handleDeleteConfirm}
          isLoading={deleteLoading}
          options={{
            title: 'Delete Group Mapping',
            description: `Are you sure you want to delete the mapping for "${deleteMapping[getGroupKeyField(identifierType)]}"?`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
          }}
        />
      )}
    </>
  );
};

SSOGroupMappingSection.propTypes = {
  level: PropTypes.string.isRequired,
  levelId: PropTypes.string.isRequired,
  partnerId: PropTypes.string,
  groupId: PropTypes.string,
  ssoProvider: PropTypes.string.isRequired,
  identifierType: PropTypes.string,
  onBack: PropTypes.func.isRequired,
};

export default SSOGroupMappingSection;
